import { useInfiniteQuery } from 'react-query';

import { ArchivedManualItem } from '../../_types/manuals/draft';
import { ApiCollectionResponse } from 'types/api_response';
import { baseApiUrl, fetcher, ServerErrorResponse, createParamString } from 'data_sources/constants';
import contentHelper from 'helpers/contentHelper';

export default function useLegacyArchivedManuals<T extends ArchivedManualItem>(
  organizationId: string,
  params = {},
  options: object = {},
  onError?: (errors: ServerErrorResponse) => void
) {
  const url = `${baseApiUrl()}/lexipub/manuals/archived/v1/legacy-manuals`;

  return useInfiniteQuery<ApiCollectionResponse<T>, ServerErrorResponse>(
    ['legacy_archived_manuals', organizationId, params],
    async ({ pageParam, signal }): Promise<ApiCollectionResponse<T>> => {
      try {
        const paramString = createParamString(params);
        const data: ApiCollectionResponse<T> = await fetcher({
          method: 'get',
          url: pageParam || `${url}?${paramString}`,
          options: { signal },
        });

        return convertResults(data) as ApiCollectionResponse<T>;
      } catch (error: unknown) {
        if (onError && typeof error === 'object' && error !== null && 'errorDetails' in error) {
          onError(error as ServerErrorResponse);
        }
        throw error;
      }
    },
    {
      keepPreviousData: true,
      getNextPageParam: (lastPage) => {
        if (lastPage.offset && lastPage.offset < (lastPage.count || 0) && lastPage.content.length > 0) {
          const paramString = createParamString({
            ...params,
            offset: lastPage.offset,
          });
          return `${url}?${paramString}`;
        }
      },
      ...options,
    }
  );
}

const convertResults = (data: any) => {
  const count =
    data.totalElements ?? data.count ?? data.content?.length ?? 0;
  return {
    count,
    offset: count,
    content: (data.content ?? []).map((x: any) => ({
    id: x.id,
    manualType: {
      key: x.manualType?.key,
      name: x.manualType?.value,
    },
    name: contentHelper.decodeHtmlEntities(x.name || ''),
    organizationId: x.organizationId,
    fromDate: x.fromDate,
    toDate: x.toDate,
    vertical: {
      key: x.vertical?.key,
      name: x.vertical?.value,
    },
  })),
  };
};
